import _ from 'underscore';

import {toSnakeCase} from 'common/utils';

// Clone an instance of a type and assign a new value to the specified
// property. This should be done with Object.assign (or an object literal
// that copies the old properties using the spread operator) for
// a plain object.
export const cloneAndAssign = (instance, property, value) => {
  if (instance !== null) {
    const copy = instance.clone();
    copy[property] = value;
    return copy;
  }
  return null;
};

// Chain any number of reducers together so they all operate on the same state.
// This is different than combineReducers, in which each reducer operates on a
// different property of the state. Reducers will be called in the order they
// are passed in.
export function chainReducers(...reducers) {
  return reducers.reduce((memo, reducer) =>
    (state, action) => reducer(memo(state, action), action)
  );
}

// Create a reducer for actions that fetch data from a remote source. "keys" is
// an array of properties that uniquely identifies the fetched data. For
// example, a list of routes might be keyed on ['agencyId'] while a list of
// trips might be keyed on ['agencyId', 'feedId', 'routeId']
export function createFetchListReducer({
  name,
  pluralName = `${name}s`,
  keys = [],
  defaultItems = {},
  reduceBaseListResource = false,
}) {
  const reducerNamePlural = toSnakeCase(pluralName).toUpperCase();
  const reducerName = toSnakeCase(name).toUpperCase();

  return (state = {}, action) => {
    switch (action.type) {
      case `CLEAR_${reducerNamePlural}`:
        return {
          ...state,
          items: defaultItems,
          ..._.object(keys, _.map(keys, () => null)),
        };
      case `REQUEST_${reducerNamePlural}`:
        return {
          ...state,
          isFetching: true,
          ..._.pick(action, ...keys), // copy the key properties from the action to the state
        };
      case `RECEIVE_${reducerNamePlural}`:
        // only write items if keys match, otherwise it's probably a stale response
        if (_.isEqual(_.pick(state, ...keys), _.pick(action, ...keys))) {
          if (reduceBaseListResource) {
            return {
              ...state,
              isFetching: false,
              items: action.items,
              total: action.total,
              nextCursor: action.nextCursor,
              currentPageIndex: action.pageIndex,
              cursors: action.cursors,
            };
          }
          return {
            ...state,
            isFetching: false,
            hasMore: action.hasMore || false,
            items: action.merge ? {...state.items, ...action.items} : action.items,
          };
        }
        return state;
      case `RECEIVE_SINGLE_${reducerName}`: {
        // only write items if keys match, otherwise it's probably a stale response
        if (_.isEqual(_.pick(state, ...keys), _.pick(action, ...keys))) {
          const items = {...state.items};
          items[action.id] = action.item;
          return {
            ...state,
            isFetching: false,
            items,
          };
        }
        return state;
      }
      case `REQUEST_REMOVE_${reducerName}`:
        return {
          ...state,
          isSaving: true,
        };
      case `RECEIVE_REMOVE_${reducerName}`:
        return {
          ...state,
          isSaving: false,
          items: _.omit(state.items, action.id),
        };
      default:
        return state;
    }
  };
}

export function createPaginatedItemReducer({
  name,
  pluralName = `${name}s`,
  keys = [],
  defaultItems = [],
}) {
  const reducerNamePlural = toSnakeCase(pluralName).toUpperCase();

  return (state = {}, action) => {
    const {index, items, limit, total, append} = action;

    switch (action.type) {
      case `CLEAR_${reducerNamePlural}`:
        return {
          ...state,
          items: defaultItems,
          total: null,
          index: null,
          limit: null,
          ..._.object(keys, _.map(keys, () => null)),
        };
      case `REQUEST_PAGE_OF_${reducerNamePlural}`:
        return {
          ...state,
          isFetching: true,
          // copy the key properties from the action to the state
          ..._.pick(action, 'index', ...keys),
        };
      case `RECEIVE_PAGE_OF_${reducerNamePlural}`:
        return {
          ...state,
          isFetching: false,
          isSaving: false,
          index,
          items: append ? state.items.concat(items) : items,
          limit,
          total,
        };
      case `REQUEST_ALL_${reducerNamePlural}`:
        return {
          ...state,
          isFetching: true,
          ..._.pick(action, ...keys),
        };
      case `RECEIVE_ALL_${reducerNamePlural}`:
        return {
          ...state,
          items,
          total,
          index: null,
          limit: null,
          isFetching: false,
        };
      default:
        return state;
    }
  };
}

export const MAX_PAST_SIZE = 50;

const initializeDraft = (item) => ({
  past: [],
  present: {
    model: item,
    errors: [],
  },
  future: [],
});

export function updateDraftPast(state) {
  const past = state.draft.past.slice();
  if (state.draft.present.model !== null) {
    past.push({
      model: state.draft.present.model.clone(),
      errors: state.draft.present.errors.slice(),
    });
  }
  return past.slice(0, MAX_PAST_SIZE);
}

export function cloneDraft(draft) {
  return {
    past: draft.past.slice(),
    present: {
      model: draft.present.model.clone(),
      errors: draft.present.errors.slice(),
    },
    future: draft.future.slice(),
  };
}

export function handleUndo(draft) {
  const past = draft.past.slice();
  if (past.length > 0) {
    const model = draft.present.model.clone();
    const errors = draft.present.errors.slice();
    const newPresent = past.pop();
    const newModel = newPresent.model.clone();
    const newErrors = newPresent.errors.slice();
    const newFuture = draft.future.slice();
    newFuture.push({
      model,
      errors,
    });
    return {
      past,
      present: {
        model: newModel,
        errors: newErrors,
      },
      future: newFuture,
    };
  }
  return cloneDraft(draft);
}

export function handleRedo(draft) {
  const future = draft.future.slice();
  if (future.length > 0) {
    const model = draft.present.model.clone();
    const errors = draft.present.errors.slice();
    const newFuture = future.pop();
    const newModel = newFuture.model.clone();
    const newErrors = newFuture.errors.slice();
    const newPast = draft.past.slice();
    newPast.push({
      model,
      errors,
    });
    return {
      past: newPast,
      present: {
        model: newModel,
        errors: newErrors,
      },
      future,
    };
  }
  return cloneDraft(draft);
}

export function createDraftReducer({
  name,
  DataType,
  idProp = `${toSnakeCase(name)}_id`,
}) {
  const reducerName = toSnakeCase(name).toUpperCase();
  return (state = {}, action) => {
    switch (action.type) {
      case `NEW_${reducerName}_DRAFT`:
        return {
          ...state,
          draft: initializeDraft(new DataType({})),
        };
      case `SET_${reducerName}_DRAFT`:
        return {
          ...state,
          draft: initializeDraft(action.item.clone()),
        };
      case `EDIT_${reducerName}_DRAFT`: {
        const past = updateDraftPast(state);
        return {
          ...state,
          draft: {
            past,
            present: {
              model: cloneAndAssign(state.draft.present.model, action.key, action.value),
              errors: state.draft.present.errors.slice(),
            },
            future: [],
          },
        };
      }
      case `SAVE_${reducerName}_DRAFT`:
        return {
          ...state,
          isSaving: true,
        };
      case `UNDO_${reducerName}_DRAFT`:
        return {
          ...state,
          draft: handleUndo(state.draft),
        };
      case `REDO_${reducerName}_DRAFT`:
        return {
          ...state,
          draft: handleRedo(state.draft),
        };
      case `CLEAR_${reducerName}_DRAFT`:
        return {
          ...state,
          draft: initializeDraft(null)
        };
      case `RECEIVE_${reducerName}_DRAFT_ERRORS`:
        return {
          ...state,
          isSaving: false,
          draft: {
            past: state.draft.past.slice(),
            present: {
              model: state.draft.present.model.clone(),
              errors: _.isArray(action.errors) ? action.errors.slice() : [action.errors],
            },
            future: [],
          },
        };
      case `RECEIVE_${reducerName}_ONE_OFF_SUBMISSION_ERRORS`:
        return {
          ...state,
          isSaving: false,
          oneOffSubmissionErrors:
            _.isArray(action.errors) ? action.errors.slice() : [action.errors],
        };
      case `RECEIVE_${reducerName}`:
        /**
         * The state may be an array (if this is paginated data) or
         * an object (if it's not), so both cases have to be handled.
         * Updating vs adding looks different for each case.
         */
        try {
          let newItems;

          if (_.isArray(state.items)) {
            newItems = [...state.items];

            /**
             * Check if there's an item with the same ID in the state
             * already. If there is, update it by setting the value
             * on newItems. If there isn't, add it.
             */
            const index = _.findIndex(
              state.items,
              item => item[idProp] === action.item[idProp],
            );
            if (index >= 0) {
              newItems[index] = action.item;
            } else if (_.isArray(newItems)) {
              newItems.push(action.item);
            }
          } else {
            /**
             * If the state is an object, adding and updating are
             * the same operation, so we don't need to check if
             * it's already there.
             */
            newItems = {
              ...state.items,
              [action.item[idProp]]: action.item,
            };
          }

          return {
            ...state,
            isSaving: false,
            isFetching: false,
            items: newItems,
          };
        } catch (e) {
          return state;
        }
      case `RECEIVE_${reducerName}_DRAFT`:
        return {
          ...state,
          // TODO: INCREMENT / DECREMENT IS FETCHING
          // better support multiple requests going out (overall isFetching state?)
          isFetching: false,
          draft: initializeDraft(action.item.clone()),
        };
      case `REQUEST_${reducerName}_DETAILS`:
        return {
          ...state,
          isFetching: true,
        };
      default:
        return state;
    }
  };
}

// Create a reducer for actions that fetch data from a remote source. "keys" is
// an array of properties that uniquely identifies the fetched data. For
// example, a list of routes might be keyed on ['agencyId'] while a list of
// trips might be keyed on ['agencyId', 'feedId', 'routeId']
export function createSimpleFetchReducer({
  name,
  keys = [],
  defaultData = {},
}) {
  const baseReducerName = toSnakeCase(name).toUpperCase();

  return (state = {}, action) => {
    switch (action.type) {
      case `CLEAR_${baseReducerName}`:
        return {
          ...state,
          data: defaultData,
          error: null,
          ..._.object(keys, _.map(keys, () => null)),
        };
      case `REQUEST_${baseReducerName}`:
        return {
          ...state,
          isFetching: true,
          ..._.pick(action, ...keys), // copy the key properties from the action to the state
        };
      case `RECEIVE_${baseReducerName}`:
        // only write items if keys match, otherwise it's probably a stale response
        if (_.isEqual(_.pick(state, ...keys), _.pick(action, ...keys))) {
          return {
            ...state,
            isFetching: false,
            data: action.data ? action.data : defaultData,
            error: action.error ? action.error : null,
          };
        }
        return state;
      default:
        return state;
    }
  };
}



// WEBPACK FOOTER //
// ./src/common/reducers/utils.js